import api from './api'

export interface User {
  id: string
  email: string
  name: string
  phone?: string
  avatarUrl?: string
  isActive: boolean
  isMaster: boolean
  companiesCount: number
  createdAt: string
  updatedAt: string
}

export interface UserDetails extends User {
  companies: Array<{
    id: string
    type: 'shipper' | 'freight-forwarder'
    name: string
    trading_name?: string
    document?: string
    role: string
  }>
}

export interface CreateInviteData {
  email: string
  tenantId: string
  companyId: string
  companyType: 'shipper' | 'freight-forwarder'
  role: string
}

export interface Invite {
  id: string
  email: string
  token: string
  role: string
  companyId: string
  companyType: 'shipper' | 'freight-forwarder'
  tenantId: string
  tenantName?: string
  expiresAt: string
  acceptedAt?: string
  createdAt: string
  invitedByName?: string
}

/**
 * List all users
 */
export async function listUsers(): Promise<User[]> {
  const response = await api.get('/users')
  return response.data
}

/**
 * Get user by ID with detailed info
 */
export async function getUserById(id: string): Promise<UserDetails> {
  const response = await api.get(`/users/${id}`)
  return response.data
}

/**
 * Invite a master admin (master-only). Sends an email verification; master
 * access is only activated after the recipient confirms.
 */
export async function createMaster(data: { name: string; email: string; password: string }): Promise<{ message: string; email: string; pendingVerification?: boolean }> {
  const response = await api.post('/master/create-master', data)
  return response.data
}

/**
 * Public: fetch master-grant invite info by token (for the confirm page).
 */
export async function getMasterGrant(token: string): Promise<{ email: string; used: boolean; expired: boolean }> {
  const response = await api.get(`/auth/master-grant?token=${encodeURIComponent(token)}`)
  return response.data
}

/**
 * Public: confirm a master grant — activates master access for the account.
 */
export async function confirmMasterGrant(token: string): Promise<{ message: string; redirectTo: string }> {
  const response = await api.post('/auth/confirm-master', { token })
  return response.data
}

/**
 * Hard-delete a user (master-only). Throws (409) if the user has history.
 */
export async function deleteUser(id: string): Promise<void> {
  await api.delete(`/master/users/${id}`)
}

/**
 * Activate / deactivate a user account (master-only).
 */
export async function setUserActive(id: string, isActive: boolean): Promise<void> {
  await api.patch(`/master/users/${id}/active`, { isActive })
}

/**
 * Create company invite
 */
export async function createInvite(data: CreateInviteData): Promise<Invite> {
  const response = await api.post('/users/invites', data)
  return response.data
}

/**
 * List invites for a company
 */
export async function listInvites(companyId: string, companyType: 'shipper' | 'freight-forwarder'): Promise<Invite[]> {
  const queryParams = new URLSearchParams()
  queryParams.set('companyId', companyId)
  queryParams.set('companyType', companyType)
  const response = await api.get(`/users/invites/list?${queryParams.toString()}`)
  return response.data
}

/**
 * List all invites (master admin only)
 */
export async function listAllInvites(): Promise<Invite[]> {
  const response = await api.get('/users/invites')
  return response.data
}

/**
 * Get invite by token
 */
export async function getInviteByToken(token: string): Promise<Invite & { companyName: string }> {
  const response = await api.get(`/users/invites/get?token=${token}`)
  return response.data
}

/**
 * Accept invite
 */
export async function acceptInvite(token: string): Promise<{ success: boolean; invite: Invite }> {
  const response = await api.post('/users/invites/accept', { token })
  return response.data
}

/**
 * Resend invite email
 */
export async function resendInvite(inviteId: string): Promise<{ success: boolean; message: string }> {
  const response = await api.post(`/users/invites/${inviteId}/resend`)
  return response.data
}
